import type { FingerprintImageMetadata } from '@cnapse/shared/schemas.js';
import type { UploadedFile } from '../components/ui/FileUpload';
import type { FilePreviewDescriptor } from '../types/files';
import { resolveFilePreviewUrl } from './filePreview';

export type FingerprintUploadedFile = UploadedFile & {
  metadata: FingerprintImageMetadata;
};

function buildMetadata(source: FingerprintImageMetadata): FingerprintUploadedFile['metadata'] {
  return {
    objectKey: source.objectKey,
    originalName: source.originalName || source.objectKey.split('/').pop() || 'fingerprint.jpg',
    mimeType: source.mimeType || 'image/jpeg',
    size: source.size || 0,
    uploadedAt: source.uploadedAt,
    ...(source.fileId != null ? { fileId: source.fileId } : {}),
    ...(source.ocrConfidence != null ? { ocrConfidence: source.ocrConfidence } : {}),
    ...(source.blurScore != null ? { blurScore: source.blurScore } : {}),
    ...(source.isVerifiedByUser != null ? { isVerifiedByUser: source.isVerifiedByUser } : {}),
    ...(source.userMarkedAsBad != null ? { userMarkedAsBad: source.userMarkedAsBad } : {}),
  };
}

export function createFingerprintPlaceholder(
  source: FingerprintImageMetadata,
): FingerprintUploadedFile {
  return { url: '', metadata: buildMetadata(source) };
}

export function buildFingerprintPlaceholders(
  images: FingerprintImageMetadata[],
): FingerprintUploadedFile[] {
  return images
    .filter((img) => img.objectKey?.trim())
    .map((img) => createFingerprintPlaceholder(img));
}

export interface LoadFingerprintPreviewsOptions {
  onFileReady: (file: FingerprintUploadedFile) => void;
  isCancelled?: () => boolean;
}

/**
 * Resolve preview URLs in parallel; invokes onFileReady as each image finishes (not after the full batch).
 */
export function loadFingerprintPreviewsProgressive(
  images: FingerprintImageMetadata[],
  options: LoadFingerprintPreviewsOptions,
): void {
  for (const img of images) {
    if (!img.objectKey?.trim()) {
      continue;
    }

    const descriptor: FilePreviewDescriptor = {
      fileId: img.fileId,
      objectKey: img.objectKey,
    };

    void resolveFilePreviewUrl(descriptor).then((url) => {
      if (options.isCancelled?.() || !url) {
        return;
      }
      options.onFileReady({
        url,
        metadata: buildMetadata(img),
      });
    });
  }
}

/**
 * Attach preview URLs to rows that lack them; updates each file via onFileReady as it resolves.
 */
export function attachPreviewUrlsProgressive(
  files: FingerprintUploadedFile[],
  options: LoadFingerprintPreviewsOptions,
): void {
  for (const file of files) {
    if (file.url) {
      continue;
    }
    const descriptor: FilePreviewDescriptor = {
      fileId: file.metadata.fileId,
      objectKey: file.metadata.objectKey,
    };
    void resolveFilePreviewUrl(descriptor).then((url) => {
      if (options.isCancelled?.() || !url) {
        return;
      }
      options.onFileReady({ ...file, url });
    });
  }
}
